import React, { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Users, Sparkles, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLATFORMS = ["Instagram", "YouTube", "TikTok", "LinkedIn", "Twitter / X", "Snapchat"];

const initial = {
  name: "",
  email: "",
  handle: "",
  phone: "",
  primaryPlatform: "Instagram",
  followers: "",
  niches: "",
  location: "",
  languages: "",
  samples: "",
  about: "",
};

const JoinNetwork = () => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.handle || !form.primaryPlatform) {
      toast.error("Please fill in name, email, handle and platform.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/creator-applications`, {
        ...form,
        followers: form.followers ? Number(form.followers) : 0,
      });
      setSubmitted(true);
      setForm(initial);
      toast.success("Application received. Our team will review within 5 business days.");
    } catch (_e) {
      toast.error("Could not submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="join" className="border-t border-white/5 relative">
      <div className="absolute inset-0 bg-radial-blue pointer-events-none opacity-40" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-start">
          <div className="lg:col-span-2">
            <p className="text-xs uppercase tracking-[0.2em] text-blue-400 mb-3">
              For creators
            </p>
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-zinc-50"
              data-testid="join-heading"
            >
              Join the Bagfluence creator network.
            </h2>
            <p className="mt-4 text-zinc-400 max-w-md">
              We work with creators who care about craft, consistency and brand respect.
              If that's you — apply below. We typically respond within 5 business days.
            </p>

            <div className="mt-10 space-y-4">
              {[
                { icon: Sparkles, title: "Premium brand partnerships", desc: "Direct deals with Nykaa, boAt, Swiggy, CRED, Mamaearth and 50+ more." },
                { icon: Users, title: "Pricing & negotiation support", desc: "We negotiate your rate card. You focus on the content." },
                { icon: CheckCircle2, title: "Faster, cleaner payouts", desc: "Net-15 payouts, transparent reporting, no chasing." },
              ].map((b) => (
                <div key={b.title} className="flex items-start gap-3">
                  <div className="h-9 w-9 shrink-0 rounded-lg bg-blue-600/10 border border-blue-500/20 flex items-center justify-center">
                    <b.icon className="h-4 w-4 text-blue-400" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-zinc-100">{b.title}</div>
                    <div className="text-sm text-zinc-400">{b.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <form
            onSubmit={submit}
            className="lg:col-span-3 rounded-2xl border border-white/10 bg-zinc-950/70 backdrop-blur-xl p-6 lg:p-8 space-y-4"
            data-testid="join-form"
          >
            {submitted ? (
              <div className="text-center py-12" data-testid="join-success">
                <div className="h-12 w-12 mx-auto rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
                  <CheckCircle2 className="h-6 w-6 text-emerald-400" />
                </div>
                <h3 className="mt-4 text-xl font-semibold tracking-tight text-zinc-50">
                  Application received.
                </h3>
                <p className="mt-2 text-sm text-zinc-400 max-w-sm mx-auto">
                  Our team will review your submission and reply within 5 business days. Welcome
                  to the family.
                </p>
                <Button
                  className="mt-6 bg-blue-600 hover:bg-blue-500 text-white"
                  onClick={() => setSubmitted(false)}
                  data-testid="join-submit-another"
                >
                  Submit another
                </Button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="cr-name" className="text-zinc-300">Full name</Label>
                    <Input
                      id="cr-name"
                      placeholder="Aanya Sharma"
                      value={form.name}
                      onChange={onChange("name")}
                      className="mt-1.5 bg-white/[0.03] border-white/10 text-zinc-100 placeholder:text-zinc-600"
                      data-testid="join-input-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="cr-handle" className="text-zinc-300">Primary handle</Label>
                    <Input
                      id="cr-handle"
                      placeholder="@yourhandle"
                      value={form.handle}
                      onChange={onChange("handle")}
                      className="mt-1.5 bg-white/[0.03] border-white/10 text-zinc-100 placeholder:text-zinc-600"
                      data-testid="join-input-handle"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="cr-email" className="text-zinc-300">Email</Label>
                    <Input
                      id="cr-email"
                      type="email"
                      placeholder="you@email.com"
                      value={form.email}
                      onChange={onChange("email")}
                      className="mt-1.5 bg-white/[0.03] border-white/10 text-zinc-100 placeholder:text-zinc-600"
                      data-testid="join-input-email"
                    />
                  </div>
                  <div>
                    <Label htmlFor="cr-phone" className="text-zinc-300">Phone</Label>
                    <Input
                      id="cr-phone"
                      placeholder="+91 …"
                      value={form.phone}
                      onChange={onChange("phone")}
                      className="mt-1.5 bg-white/[0.03] border-white/10 text-zinc-100 placeholder:text-zinc-600"
                      data-testid="join-input-phone"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-zinc-300">Primary platform</Label>
                    <Select
                      value={form.primaryPlatform}
                      onValueChange={(v) => setForm({ ...form, primaryPlatform: v })}
                    >
                      <SelectTrigger
                        className="mt-1.5 bg-white/[0.03] border-white/10 text-zinc-100"
                        data-testid="join-select-platform"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-white/10">
                        {PLATFORMS.map((p) => (
                          <SelectItem key={p} value={p}>{p}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="cr-followers" className="text-zinc-300">Followers (primary)</Label>
                    <Input
                      id="cr-followers"
                      type="number"
                      placeholder="120000"
                      value={form.followers}
                      onChange={onChange("followers")}
                      className="mt-1.5 bg-white/[0.03] border-white/10 text-zinc-100 placeholder:text-zinc-600"
                      data-testid="join-input-followers"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="cr-niches" className="text-zinc-300">Niches</Label>
                    <Input
                      id="cr-niches"
                      placeholder="Fashion, Beauty"
                      value={form.niches}
                      onChange={onChange("niches")}
                      className="mt-1.5 bg-white/[0.03] border-white/10 text-zinc-100 placeholder:text-zinc-600"
                      data-testid="join-input-niches"
                    />
                  </div>
                  <div>
                    <Label htmlFor="cr-location" className="text-zinc-300">City</Label>
                    <Input
                      id="cr-location"
                      placeholder="Mumbai"
                      value={form.location}
                      onChange={onChange("location")}
                      className="mt-1.5 bg-white/[0.03] border-white/10 text-zinc-100 placeholder:text-zinc-600"
                      data-testid="join-input-location"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="cr-samples" className="text-zinc-300">Best work / portfolio links</Label>
                  <Input
                    id="cr-samples"
                    placeholder="https://… (Instagram, YouTube, drive links)"
                    value={form.samples}
                    onChange={onChange("samples")}
                    className="mt-1.5 bg-white/[0.03] border-white/10 text-zinc-100 placeholder:text-zinc-600"
                    data-testid="join-input-samples"
                  />
                </div>

                <div>
                  <Label htmlFor="cr-about" className="text-zinc-300">Tell us about you</Label>
                  <Textarea
                    id="cr-about"
                    rows={3}
                    placeholder="Why you, what you make, who watches you."
                    value={form.about}
                    onChange={onChange("about")}
                    className="mt-1.5 bg-white/[0.03] border-white/10 text-zinc-100 placeholder:text-zinc-600"
                    data-testid="join-input-about"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-blue-600 hover:bg-blue-500 text-white h-11 glow-blue"
                  data-testid="join-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting…
                    </>
                  ) : (
                    "Apply to the network"
                  )}
                </Button>
                <p className="text-[11px] text-zinc-500 text-center">
                  By applying, you agree to be contacted by the Bagfluence team about partnerships.
                </p>
              </>
            )}
          </form>
        </div>
      </div>
    </section>
  );
};

export default JoinNetwork;
